import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { ImageUploaderService } from '@app/shared/services/image-uploader.service'
import { TranslateService } from '@ngx-translate/core'
import { MessageService } from 'primeng/api'
import { Subscription } from 'rxjs'

interface UploadResponse {
  success: boolean
  data?: { link: string }
  errorText?: string
}

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class FileUploaderComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private destroyRef = inject(DestroyRef)
  constructor(private uploader: ImageUploaderService, private messageService: MessageService, private translateService: TranslateService) {}

  @Input() formControl?: FormControl
  @Input() disabled = false
  @Output() loadingChange = new EventEmitter<boolean>()
  @Output() urlChange = new EventEmitter<string>()
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>

  fileName: string | null = null
  loading = false
  isDragging = false

  private request: ReturnType<ImageUploaderService['upload']> | undefined
  private onChange: (value: string | null) => void = () => {/* noop */}
  private onTouched: () => void = () => {/* noop */}
  private formValueChangesSubscription?: Subscription

  ngOnInit(): void {
    // If used together with a form control
    if (this.formControl) {
      this.fileName = this.formControl.value as string | null
      this.formValueChangesSubscription = this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
        this.fileName = value
      })
    }
  }

  writeValue(value: string | null): void {
    this.fileName = value
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  onDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.disabled && !this.loading) {
      this.isDragging = true
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false
  }

  onDrop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false

    if (this.disabled || this.loading) {
      return
    }

    const files = event.dataTransfer?.files
    if (!files?.length) {
      return
    }

    const file = files[0]
    this.handleFile(file)
  }

  // Extract common file handling logic to a separate method
  handleFile(file: File) {
    const allowedTypes = [
      'image/',
      'video/',
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ]

    const fileType = file.type

    if (!allowedTypes.some((type) => fileType.startsWith(type))) {
      this.messageService.add({ severity: 'error', summary: 'Invalid File Type', detail: 'Only media and text files are allowed.', life: 10000 })
      return
    }

    this.loading = true
    this.loadingChange.emit(true)

    this.request = this.uploader.upload(file, 'mediaUploads')
    this.request.response
      .then((response) => {
        const e = response as UploadResponse
        if (e.success) {
          this.fileName = e.data!.link

          // Call the existing onChange handler (for ControlValueAccessor)
          this.onChange(this.fileName)
          this.formControl?.setValue(this.fileName)

          // Also emit with our custom event
          this.urlChange.emit(this.fileName)
        } else {
          this.messageService.add({ severity: 'error', summary: 'File Upload Error', detail: e.errorText, life: 10000 })
        }
      })
      .finally(() => {
        this.loadingChange.emit(false)
        this.loading = false
      })

    this.onTouched()
  }

  onFileSelected(event: Event) {
    const files = (event.target as HTMLInputElement | null)?.files
    if (!files?.length) {
      return
    }

    const file = files[0]
    this.handleFile(file)
  }

  reset(e: Event) {
    e.preventDefault()
    this.fileName = null

    // Reset the file input if it exists
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = ''
    }

    // Call the existing onChange handler (for ControlValueAccessor)
    this.onChange(this.fileName)
    this.formControl?.reset()

    // Also emit with our custom event to notify parent about the reset
    this.urlChange.emit('')
  }

  public completeReset() {
    this.fileName = null

    // Reset the file input if it exists
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = ''
    }

    // Reset the form control and call change handler
    this.onChange(null)
    this.formControl?.reset()

    // Emit the URL change
    this.urlChange.emit('')
  }

  getFileType(fileName: string | null): string {
    if (!fileName) {
      return ''
    }
    const extension = fileName.split('.').pop()?.toUpperCase()
    const fileLabel = this.translateService.instant('GENERAL.FILE')
    return extension
      ? this.translateService.currentLang === 'ar' || this.translateService.currentLang === 'he'
        ? `${fileLabel} ${extension}`
        : `${extension} ${fileLabel}`
      : this.translateService.instant('COMMON.MESSAGES.UNKNOWN_FILE_TYPE')
  }

  getFileIconClass(fileName: string | null): string {
    if (!fileName) {
      return 'pi pi-file'
    }
    const extension = fileName.split('.').pop()?.toLowerCase()
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'pi pi-image'
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'pi pi-video'
      case 'pdf':
        return 'pi pi-file-pdf'
      case 'doc':
      case 'docx':
        return 'pi pi-file-word'
      case 'xls':
      case 'xlsx':
        return 'pi pi-file-excel'
      case 'txt':
        return 'pi pi-file'
      default:
        return 'pi pi-file'
    }
  }

  ngOnDestroy(): void {
    this.formValueChangesSubscription?.unsubscribe()
  }
}
